import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
const { arrayOf, bool, object } = PropTypes;
import classNames from 'classnames';

import { ASSET_NAME } from './LandingPage.duck';
import PageBuilder from '../PageBuilder/PageBuilder';

import SearchResultsPanel from '../SearchPage/SearchResultsPanel/SearchResultsPanel';
import { ListingCard } from '../../components';

import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { useConfiguration } from '../../context/configurationContext';

import FallbackPage from './FallbackPage';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { injectIntl } from '../../util/reactIntl';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import css from './LandingPage.module.css';
import searchPanelCss from '../SearchPage/SearchResultsPanel/SearchResultsPanel.module.css';

const cardRenderSizes = isMapVariant => {
  if (isMapVariant) {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) ${panelMediumWidth}vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  } else {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      `(max-width: 1439px) 26vw`,
      `(max-width: 1920px) 18vw`,
      `14vw`,
    ].join(', ');
  }
};

import { PrimaryButtonInline, NamedLink } from '../../components';
import { Link } from 'react-router-dom';
import NewMediaHeroSection from './NewMediaHeroSection';

import logoStacked from '../../assets/UI-LOGO-LANDING-STACKED-BLUE.svg';
import logoInline from '../../assets/UI-LOGO-LANDING-BLUE.svg';

const LogoSection = props => {
  const { isScrolled } = props;
  return (
    <div
      className={css.headerLogoContainer}
      style={{
        opacity: isScrolled ? 0 : 1,
        pointerEvents: isScrolled ? 'none' : 'auto',
      }}
    >
      <img
        src={logoInline}
        alt="Used Icons"
        className={css.headerLogoInline}
        fetchpriority="high"
        loading="eager"
      />
      <img
        src={logoStacked}
        alt="Used Icons"
        className={css.headerLogoStacked}
        fetchpriority="high"
        loading="eager"
      />
    </div>
  );
};

const TopTextSection = props => {
  const { sectionId } = props;

  return (
    <div id={sectionId} className={css.topTextSectionWrapper}>
      <h3 style={{ padding: 0, margin: 0 }}>
        <strong>Timeless design deserves a second life.</strong> A curated marketplace for pre-loved
        design.
        <span className={css.desktopBreak}>
          <br />
        </span>{' '}
        We orchestrate the sale from door to door. Rooted in Stockholm, trading everywhere.
      </h3>
    </div>
  );
};

const QuickLinks = () => {
  return (
    <>
      <div className={css.quickLinksContainer}>
        <div className={css.quickLinksLeft}>
          <p>Welcome!</p>
        </div>
        <div className={css.quickLinksRight}>
          <Link to="/s">Shop all</Link>
          <Link to="/s?sort=createdAt">Newest</Link>
          <Link className={css.priceLink} to="/s?sort=-price">
            Price
          </Link>
        </div>
      </div>
      <hr
        style={{
          border: 'none',
          borderTop: '1.5px solid #E8E8E8',
          margin: '10px 0 32px 0',
        }}
      />
    </>
  );
};

const SkeletonCard = () => (
  <div className={css.skeletonCard}>
    <div className={css.skeletonImage} />
    <div className={css.skeletonInfo}>
      <div className={css.skeletonTitle} />
      <div className={css.skeletonPrice} />
    </div>
  </div>
);

const ListingSection = props => {
  const { sectionId, listingData } = props;

  if (!listingData || listingData.length === 0) {
    return (
      <div id={sectionId}>
        <div className={searchPanelCss.listingCards}>
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div id={sectionId}>
      <SearchResultsPanel
        className={searchPanelCss.searchListingsPanel}
        listings={listingData}
        pagination={null}
        isMapVariant={false}
      />
    </div>
  );
};

// Commented out old FullWidthImageSection
const FullWidthImageSection = props => {
  const { sectionId } = props;

  return (
    <div
      id={sectionId}
      className={css.fullWidthImageContainer}
      style={{ position: 'relative', width: '100%' }}
    >
      <NamedLink
        name="SearchPage"
        className={classNames(css.navigationLink)}
        // to={{ pathname: '/s', search: '?pub_categoryLevel1=seating' }}
        to={{ pathname: '/s' }}
      >
        <picture>
          <source srcSet={require('../../assets/Marketplace_Banner.webp')} type="image/webp" />
          <img
            className={css.desktopFullWidthImage}
            src={require('../../assets/Marketplace_Banner.png')}
            alt="take a seat"
            loading="lazy"
          />
        </picture>
        <picture>
          <source
            srcSet={require('../../assets/Marketplace_Banner_Mobile.webp')}
            type="image/webp"
          />
          <img
            className={css.mobileFullWidthImage}
            src={require('../../assets/Marketplace_Banner_Mobile.png')}
            alt="lamp on chair"
            loading="lazy"
          />
        </picture>
      </NamedLink>
    </div>
  );
};

// The NewMediaHeroSection implementation has been moved to a separate file

const GridSection = () => {
  return (
    <div className={css.gridSectionWrapper}>
      <div id="grid-section" className={css.categoryGrid}>
        {[
          {
            image: require('../../assets/tables_landing.png'),
            webp: require('../../assets/tables_landing.webp'),
            title: 'TABLES',
            search: '?pub_categoryLevel1=tables',
          },
          {
            image: require('../../assets/decor_landing.png'),
            webp: require('../../assets/decor_landing.webp'),
            title: 'DECOR',
            search: '?pub_categoryLevel1=decor',
          },
          {
            image: require('../../assets/lighting_landing.png'),
            webp: require('../../assets/lighting_landing.webp'),
            title: 'LIGHTING',
            search: '?pub_categoryLevel1=lighting',
          },
          {
            image: require('../../assets/seating_landing.png'),
            webp: require('../../assets/seating_landing.webp'),
            title: 'SEATING',
            search: '?pub_categoryLevel1=seating',
          },
        ].map((item, index) => (
          <div key={index} style={{ textAlign: 'center' }}>
            <NamedLink name="SearchPage" to={{ search: item.search }}>
              <picture>
                <source srcSet={item.webp} type="image/webp" />
                <img
                  src={item.image}
                  alt={`Image ${index + 1}`}
                  style={{ width: '100%', height: 'auto' }}
                  loading="lazy"
                />
              </picture>
              <h2 className={css.categoryGridTitle}>{item.title}</h2>
            </NamedLink>
          </div>
        ))}
      </div>
    </div>
  );
};

const GridSectionWithButtons = props => {
  const { listing } = props;

  const items = [
    {
      image: require('../../assets/editorials_4_landing_desktop.png'),
      imageWebp: require('../../assets/editorials_4_landing_desktop.webp'),
      imageMobile: require('../../assets/editorials_4_landing_desktop.png'),
      // title: '"MY TOTAL NET WORTH IS $50 AND A WIGGLE"',
      // primaryButtonText: 'How does it work?',
      // primaryButtonLink: '/p/how-does-it-work',
      // secondaryButtonLink: '/l/new',
      // secondaryButtonText: 'Sell chairs',
    },
  ];

  const forceLargeImages = '2000px';

  return (
    <div
      id="grid-section"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        padding: '32px 0 32px 0',
        gap: '32px',
      }}
    >
      {items.map((item, index) => (
        <div key={index} style={{ position: 'relative', textAlign: 'center' }}>
          <NamedLink className={classNames(css.navigationLink)} name="EditorialsPage">
            <picture>
              <source srcSet={item.imageWebp} type="image/webp" />
              <img
                src={item.image}
                className={css.gridSectionImage}
                alt={`Image ${index + 1}`}
                loading="lazy"
              />
            </picture>
            <img
              className={css.gridSectionImageMobile}
              src={item.imageMobile}
              alt={`Image ${index + 1}`}
              loading="lazy"
            />
          </NamedLink>

          {/* <div className={css.primaryButtonOnImage}>
            <a href={item.primaryButtonLink}>
              <PrimaryButtonInline
                className={css.primaryButton}
                style={{ width: '200px' }}
              >
                {item.primaryButtonText}
              </PrimaryButtonInline>
            </a>
            <a href={item.secondaryButtonLink}>
                <PrimaryButtonInline className={css.secondaryButton}>
                  {item.secondaryButtonText}
                </PrimaryButtonInline>
              </a>
          </div>*/}
        </div>
      ))}
      {listing && (
        <ListingCard
          className={css.listingCard}
          key={listing.id.uuid}
          listing={listing}
          renderSizes={forceLargeImages}
        />
      )}
    </div>
  );
};

const TwoListingCardsGrid = props => {
  const { listingData } = props;

  const listingSubset = useMemo(() => listingData.slice(0, 8), [listingData]);

  const enhancedRenderSizes =
    '(max-width: 549px) 100vw, (max-width: 767px) 80vw, (max-width: 1439px) 50vw, (max-width: 1920px) 40vw, 33vw';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '32px',
        padding: '32px 0 32px 0',
      }}
    >
      {listingSubset.map(l => (
        <ListingCard
          className={css.listingCard}
          key={l.id.uuid}
          listing={l}
          renderSizes={enhancedRenderSizes}
        />
      ))}
    </div>
  );
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error, listings } = props;
  const pageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const config = useConfiguration();

  const pageTitle = 'Used Icons – Buy & Sell Pre-Owned Design Icons | Stockholm';
  const pageDescription =
    "Buy and sell pre-owned design icons — furniture, lighting, and accessories from the world's most celebrated designers. A marketplace for authenticated second-hand design, founded in Stockholm, trading everywhere.";
  const pageSchema = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    name: pageTitle,
    description: pageDescription,
    url: config.marketplaceRootURL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${config.marketplaceRootURL}/s?keywords={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.pageYOffset > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const predefinedOrder = [
    '696e00d5-1283-45a3-8ecf-695a6c0c6179',
    '6aa27d48-2510-427e-8009-94de37a6cbe8',
    '67eeee28-9a9b-499b-b738-1e832e5b0de2',
    '6aa6d646-dd3e-43ba-b810-af709b6c8e0e',
    '6aa6d720-7ddb-4434-ae3b-dc32976ca836',
    '6ac63333-1494-4e10-bd67-9b6988eecf5c',
    '6acbb4bf-fec5-41e9-be42-4dc4b2bf4e4d',
    '6aa27de4-f50f-44f0-aa5c-8099235baa73',
    '6ad1f6f7-7d9d-4b66-b2a0-ae81b4b82631',
    '6a68e949-3500-4b76-896d-df4772438eed',
    '6aa27667-ee73-4411-be59-5551e1f41c65',
    '67e1ae54-4ede-4829-8b0b-af7ef2753456',
    '6abf77d2-4155-4194-a1ad-4bd629298aef',
    '6ab24fcc-2fed-496c-966c-e603ac95814a',
    '69afcb9d-1ad6-48ae-b73d-b212067b24b2',
    '6aac3cb9-a3c8-4c4d-9715-a7e1cacf63ea',
    '6aa27c93-6e00-405c-a63f-5a10ccd84946',
  ];

  const sortedListingData = useMemo(() => {
    if (!listings) return []; // Fallback to an empty array if listingData is undefined

    return listings.sort((a, b) => {
      const indexA = predefinedOrder.indexOf(a.id.uuid);
      const indexB = predefinedOrder.indexOf(b.id.uuid);

      // If an ID is not in the predefinedOrder, place it at the end
      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });
  }, [listings, predefinedOrder]);

  const logoSection = {
    sectionId: 'logoheader',
    sectionType: 'logoHeader',
    isScrolled: isScrolled,
  };

  const topTextSection = {
    sectionId: 'topText',
    sectionType: 'topText',
  };

  const quickLinks = {
    sectionId: 'quickLinks',
    sectionType: 'quickLinks',
  };

  const listingSection1 = {
    sectionId: 'listingSection1',
    sectionType: 'listingSection1',
    listingData: Array.isArray(sortedListingData) ? sortedListingData.slice(0, 8) : [],
  };

  const mediaHeroSection = {
    sectionId: 'mediaHeroSection',
    sectionType: 'mediaHeroSection',
  };

  const fullWidthImageSection = {
    sectionId: 'fullWidthImageSection',
    sectionType: 'fullWidthImageSection',
  };

  const gridSection = {
    sectionId: 'grid-section',
    sectionType: 'gridSection',
  };

  const gridSectionWithButtons = {
    sectionId: 'grid-section-with-buttons',
    sectionType: 'gridSectionWithButtons',
    listing: Array.isArray(sortedListingData)
      ? sortedListingData[sortedListingData.length - 1]
      : [],
  };

  const twoListingCardsGrid = {
    sectionId: 'two-listing-cards-grid',
    sectionType: 'twoListingCardsGrid',
    listingData: listings,
  };

  const listingSection2 = {
    sectionId: 'listingSection2',
    sectionType: 'listingSection2',
    listingData: Array.isArray(sortedListingData) ? sortedListingData.slice(8, 16) : [],
  };

  const customSections = [
    logoSection,
    topTextSection,
    quickLinks,
    listingSection1,
    fullWidthImageSection,
    gridSection,
    gridSectionWithButtons,
    quickLinks,
    listingSection2,
    mediaHeroSection,
  ].filter(Boolean);

  return (
    <>
      <PageBuilder
        pageAssetsData={{
          sections: customSections,
        }}
        title={pageTitle}
        description={pageDescription}
        schema={pageSchema}
        options={{
          sectionComponents: {
            logoHeader: { component: LogoSection },
            quickLinks: { component: QuickLinks },
            topText: { component: TopTextSection },
            listingSection1: { component: ListingSection },
            mediaHeroSection: { component: NewMediaHeroSection },
            gridSection: { component: GridSection },
            gridSectionWithButtons: { component: GridSectionWithButtons },
            // twoListingCardsGrid: { component: TwoListingCardsGrid },
            listingSection2: { component: ListingSection },
            fullWidthImageSection: { component: FullWidthImageSection },

            // features: { component: SectionFeaturesLanding },
          },
        }}
        inProgress={inProgress}
        isScrolled={isScrolled}
        error={error}
        fallbackPage={<FallbackPage error={error} />}
      />
    </>
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  listings: arrayOf(propTypes.listing),
  pagination: propTypes.pagination,
  queryInProgress: bool.isRequired,
  queryFavoritesError: propTypes.error,
  queryParams: object,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { isAuthenticated } = state.auth;
  const { currentUser } = state.user;

  const {
    currentPageResultIds,
    pagination,
    queryInProgress,
    queryFavoritesError,
    queryParams,
  } = state.LandingPage;

  const listings = getListingsById(state, currentPageResultIds);

  return {
    pageAssetsData,
    inProgress,
    error,
    isAuthenticated,
    currentUser,
    currentPageResultIds,
    listings,
    pagination,
    queryInProgress,
    queryFavoritesError,
    queryParams,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps, injectIntl))(LandingPageComponent);

export default LandingPage;
